import { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import { Button, FormHelperText, Typography } from '@mui/material';
import { Search } from '@deere/fuel-react/Search.js';

const FilterListCheckbox = (props) => (
  <Box sx={{ pl: '12px', fontSize: '14px' }}>
    <FormControlLabel
      label={props.label}
      control={
        <Checkbox
          id={props.id}
          ref={props.register}
          onChange={props.onChange}
          onClick={props.onClick}
          checked={props.checked}
          indeterminate={props.indeterminate}
          disabled={props.disabled}
        />
      }
    />
  </Box>
);

export default function TreeView(props) {
  const {
    setParentMasterData,
    masterInputTreeJson,
    showInOneColumn,
    donotShowClearButton,
    showFilter,
    filterPlaceHolder,
    rpcOrUnitsFromParent,
    isEdit,
    readOnly,
    addVScrollBar,
    elementPrefix,
    selectAllFlag,
    setParentClearAllFlag,
    clearAllFlag,
    selectedUnits,
    createMGC,
    firstLevelLabel,
    secondLevelLabel,
    createScope,
    showFilterInSameCol,
    mgcsData,
    invalidError,
    filterValue,
    onFilterValueChange,
    expandAll
  } = props;
  const [masterData, setMasterData] = useState([]);
  const [filterText, setFilterText] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [refresh, setRefresh] = useState(false);
  const [isDirty, setIsDirty] = useState(null);
  const sanitizedElementPrefix = (elementPrefix || 'tree-view')
    .toString()
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9_-]+/g, '-')
    .replace(/^-+|-+$/g, '');
  const resolvedElementPrefix = sanitizedElementPrefix || 'tree-view';
  const filterSearchId = `${resolvedElementPrefix}-filter-search`;
  const sameColFilterSearchId = `${resolvedElementPrefix}-same-col-filter-search`;
  const activeFilterValue =
    filterValue !== undefined ? filterValue : filterText;

  const STATE_CHECKED = 1;
  const STATE_INDETERMINATE = -1;
  const STATE_UNCHECKED = 0;

  useEffect(() => {
    if (masterInputTreeJson) processScopeResponse(masterInputTreeJson);
  }, [masterInputTreeJson]);

  useEffect(() => {
    if (filterValue !== undefined && filterValue !== filterText) {
      filterHandler(filterValue);
    }
  }, [filterValue]);

  useEffect(() => {
    if (readOnly) {
      return;
    }
    if (clearAllFlag) {
      clearAll();
      setParentClearAllFlag && setParentClearAllFlag(false);
    } else {
      setIndeterminate();
      setParentMasterData && setParentMasterData(masterData, isDirty);
      setIsDirty(null);
    }
  }, [masterData, clearAllFlag]);

  useEffect(() => {
    if (
      masterData &&
      masterData.length > 0 &&
      selectedUnits &&
      selectedUnits.length > 0 &&
      refresh &&
      invalidError === null
    ) {
      let updatedMaster = [...masterData];
      let data = updatedMaster.map((parent) => {
        parent.children?.forEach((child) => {
          child.children?.forEach((gchild) => {
            selectedUnits.forEach((item) => {
              if (item === gchild.code) {
                gchild.checked = true;
                parent.caretOpen = true;
              }
            });
            return gchild;
          });
          return child;
        });
        return parent;
      });
      setMasterData(data);
    }
  }, [refresh, selectedUnits]);

  function getChildren(children, parent) {
    return children.map((child) => {
      return getTreeElement(child, parent);
    });
  }
  function getTreeElement(item, parent) {
    const hasChildren = item.children && item.children.length > 0;
    const elem = {
      code: item.code,
      name: createMGC ? item.nameWithHierarchicalCode : item.name,
      checked:
        parent && parent.checked === true ? parent.checked : getRpcStatus(item),
      hidden: false,
      parent,
      ...(item.disabled ? {} : { disabled: false }),
      ...(hasChildren ? { caretOpen: false } : {})
    };

    if (hasChildren) {
      elem.children = getChildren(item.children, elem);
    }

    return elem;
  }

  function processScopeResponse(result) {
    const treeElems = result.map((item) => {
      return getTreeElement(item);
    });

    // Ensure parent checked/indeterminate state is derived before initial paint.
    treeElems.forEach((item) => {
      reconcileSelfAndChildrenState(item);
    });

    if (expandAll) {
      treeElems.forEach((item) => {
        expandTreeToDepth(item, 0, 1);
      });
    }

    setMasterData(treeElems);

    selectedUnits && selectedUnits.length > 0 && setRefresh(true);
  }

  function expandTreeToDepth(item, depth, maxDepth) {
    if (!item?.children || item.children.length === 0 || depth >= maxDepth) {
      return;
    }

    item.caretOpen = true;
    item.children.forEach((child) => {
      expandTreeToDepth(child, depth + 1, maxDepth);
    });
  }

  function getRpcStatus(item) {
    if (!isEdit || !rpcOrUnitsFromParent) return false;

    const selectedParents = rpcOrUnitsFromParent.parent || [];
    const selectedChildren = rpcOrUnitsFromParent.children || [];
    const code = createMGC ? item.hierarchicalCode : item.code;

    if (selectedParents.includes(code) || selectedChildren.includes(code))
      return true;

    // An MGC saved at the commodity/major level has no minor or segment
    // codes, which implies every minor and segment below it is included.
    return (
      createMGC === true &&
      selectedParents.length === 0 &&
      selectedChildren.length === 0 &&
      !!mgcsData &&
      !!mgcsData.commodity &&
      !!mgcsData.major &&
      (rpcOrUnitsFromParent.commodities || []).includes(
        mgcsData.commodity.value
      ) &&
      (rpcOrUnitsFromParent.majors || []).includes(mgcsData.major.value)
    );
  }

  function resetTreeItem(item) {
    item.checked = false;
    item.disabled = false;

    if (item.children) {
      // Expansion state is intentionally preserved so clearing selections
      // doesn't collapse the tree the user is working in.
      setParentIndeterminateState(item, false);
      item.children.forEach((child) => resetTreeItem(child));
    }
  }
  const clearAll = () => {
    let updatedMaster = [...masterData];

    updatedMaster.forEach((item) => resetTreeItem(item));
    setMasterData(updatedMaster);
  };

  function getHierarachyIndex(indexStr) {
    return indexStr.split(',').map((index) => parseInt(index));
  }
  function findObject(data, indices) {
    let obj = data[indices[0]];

    for (let i = 1; i < indices.length; i++) {
      obj = obj.children[indices[i]];
    }
    return obj;
  }
  const handleParentClick = (indexStr) => {
    let updatedMaster = [...masterData];
    let indices = getHierarachyIndex(indexStr);
    let item = findObject(updatedMaster, indices);

    item.caretOpen = !item.caretOpen;
    setMasterData(updatedMaster);
  };

  function setParentIndeterminateState(parent, state) {
    parent.indeterminate = state;

    if (parent.children) {
      let chkBoxObj = document.getElementById(getIdAndKeyPerElement(parent));

      if (chkBoxObj !== null) {
        chkBoxObj.indeterminate = state;
      }
    }
  }
  function getIdAndKeyPerElement(item) {
    return (
      (item.parent && item.parent.code + '-') +
      (elementPrefix ? elementPrefix + '-' + item.code : item.code)
    );
  }

  function processIndeterminateForState(parent) {
    let childrenState = getChildrenState(parent);

    if (childrenState === STATE_CHECKED) {
      parent.checked = !parent.disabled;
      setParentIndeterminateState(parent, false);
    } else {
      parent.checked = false;
      setParentIndeterminateState(
        parent,
        childrenState === STATE_INDETERMINATE
      );
    }
  }
  function reconcileSelfAndChildrenState(item) {
    if (item.children) {
      item.children.forEach((child) => {
        reconcileSelfAndChildrenState(child);
      });
      processIndeterminateForState(item);
    }
  }
  function reconcileParentState(item) {
    let parent = item.parent && item.parent;

    while (parent) {
      processIndeterminateForState(parent);
      parent = parent.parent && parent.parent;
    }
  }

  function getSelectedCount(item, showBadge, msg) {
    let itemCount;
    let checkedStatus;
    const isTopLevelScopeNode = createScope && !item.parent;

    if (showBadge && !isTopLevelScopeNode) {
      if (item.children) {
        let checkedList = [];
        itemCount = item.children.length;

        item.children.forEach((child) => {
          if (child.checked) checkedList.push(child);
        });

        checkedStatus = checkedList.length;
      }
      if (
        checkedStatus !== 0 &&
        itemCount !== checkedStatus &&
        (createMGC || createScope)
      )
        return (
          <Box sx={(theme) => ({ pl: theme.fuel6Tokens.JddcSpacingMedium })}>
            <Typography
              variant="labelSmall"
              sx={(theme) => ({
                color: 'success.dark',
                fontWeight: theme.fuel6Tokens.JddcTypographyFontWeightBold
              })}
            >
              <Box component="span" sx={{ color: 'success.main' }}>
                {`${checkedStatus}/${itemCount}`}
              </Box>{' '}
              {msg}
            </Typography>
          </Box>
        );
    }
  }

  function getChildrenState(item) {
    let state = STATE_UNCHECKED;

    if (item.children) {
      let checkedStatusList = [];
      let indeterminateStatusList = [];

      item.children.forEach((child) => {
        checkedStatusList.push(child.checked);
        indeterminateStatusList.push(child.indeterminate);
      });

      const everyChildChecked = checkedStatusList.every(Boolean);

      if (everyChildChecked) state = STATE_CHECKED;
      else if (
        checkedStatusList.some(Boolean) ||
        indeterminateStatusList.some(Boolean)
      )
        state = STATE_INDETERMINATE;
    }

    return state;
  }
  const setIndeterminate = () => {
    if (masterData)
      masterData.forEach((item) => {
        reconcileSelfAndChildrenState(item);
      });
  };

  function updateCheckedSelfAndChildren(item, checkedState) {
    // Process self
    item.checked = checkedState;

    // Process Children
    if (item.children) {
      // Remove indeterminate
      !item.parent && (item.caretOpen = !item.caretOpen);
      setParentIndeterminateState(item, false);

      item.children.map((child) =>
        updateCheckedSelfAndChildren(child, checkedState)
      );
    }
  }

  const selectAll = () => {};

  /**
   * Codes are appended to node labels for MGC children and for scope units,
   * but never for scope unit groups (depth 1), which show the name only.
   */
  function showNodeCode(currentRecord, indexStr, child) {
    if (createMGC) {
      return !!child;
    }
    if (!createScope) {
      return false;
    }
    const depth = indexStr.split(',').length - 1;

    return depth >= 2;
  }

  const toggleSelectedFilter = (indexStr) => {
    let updatedMaster = [...masterData];
    let indices = getHierarachyIndex(indexStr);
    let item = findObject(updatedMaster, indices);

    updateCheckedSelfAndChildren(item, !item.checked);
    reconcileParentState(item, updatedMaster);
    setMasterData(updatedMaster);
    setIsDirty(true);
  };

  function paintTree(currentRecord, index, child) {
    if (currentRecord.hidden) return;

    let indexStr = index.toString();
    let newIndexStrBase = indexStr + ',';
    let hasChild = currentRecord.children && currentRecord.children.length > 0;

    return (
      <Box
        key={getIdAndKeyPerElement(currentRecord) || indexStr}
        sx={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}
      >
        <Box
          sx={(theme) => ({
            display: 'flex',
            alignItems: 'center',
            minWidth: 0,
            minHeight: theme.fuel6Tokens.JddcIconSizeMedium
          })}
        >
          {hasChild ? (
            <Box
              component="span"
              role="button"
              tabIndex={0}
              aria-label={currentRecord.caretOpen ? 'Collapse' : 'Expand'}
              sx={(theme) => ({
                flex: `0 0 ${theme.fuel6Tokens.JddcIconSizeSmall}`,
                width: theme.fuel6Tokens.JddcIconSizeSmall,
                cursor: 'pointer',
                color: 'text.secondary',
                display: 'inline-flex'
              })}
              onClick={() => handleParentClick(indexStr)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleParentClick(indexStr);
                }
              }}
            >
              <fuel-icon
                name={
                  currentRecord.caretOpen
                    ? 'keyboard_arrow_down'
                    : 'keyboard_arrow_right'
                }
              />
            </Box>
          ) : (
            <Box
              component="span"
              sx={(theme) => ({
                flex: `0 0 ${theme.fuel6Tokens.JddcIconSizeSmall}`,
                width: theme.fuel6Tokens.JddcIconSizeSmall,
                display: 'inline-block'
              })}
            />
          )}

          <Box
            sx={(theme) => ({
              minWidth: 0,
              fontSize: theme.fuel6Tokens.JddcTypographyBodyMediumFontSize,
              fontWeight: theme.fuel6Tokens.JddcTypographyFontWeightRegular,
              lineHeight: theme.fuel6Tokens.JddcTypographyBodyMediumLineHeight
            })}
          >
            <FilterListCheckbox
              id={getIdAndKeyPerElement(currentRecord)}
              key={getIdAndKeyPerElement(currentRecord)}
              label={
                showNodeCode(currentRecord, indexStr, child)
                  ? getHighlightedText(
                      currentRecord.name + ' - ' + currentRecord.code
                    )
                  : getHighlightedText(currentRecord.name)
              }
              checked={currentRecord.checked}
              indeterminate={currentRecord.indeterminate}
              disabled={readOnly || currentRecord.disabled}
              onChange={() => toggleSelectedFilter(indexStr)}
            />
          </Box>
          {getSelectedCount(
            currentRecord,
            true,
            createScope ? 'Units Included' : createMGC ? 'Included' : ''
          )}
        </Box>
        {currentRecord.caretOpen && currentRecord.children && (
          <Box
            sx={(theme) => ({
              display: 'flex',
              flexDirection: 'column',
              minWidth: 0,
              ml: theme.fuel6Tokens.JddcIconSizeSmall
            })}
          >
            {secondLevelLabel && (
              <Typography variant="labelSmall" sx={{ fontWeight: 'bold' }}>
                {secondLevelLabel}
              </Typography>
            )}
            {currentRecord.children.map((child, i) => {
              return paintTree(child, newIndexStrBase + i, true);
            })}
          </Box>
        )}
      </Box>
    );
  }
  const displayCheckbox = (leftOrRight) => {
    if (!masterData) return;

    let startIndex = 0;
    let endIndex =
      leftOrRight === 'justOneCol'
        ? masterData.length
        : Math.ceil(masterData.length / 2) - 1;

    if (leftOrRight === 'right') {
      startIndex = endIndex + 1;
      endIndex = masterData.length - 1;
    }

    return (
      <>
        {firstLevelLabel && (
          <Box
            sx={(theme) => ({
              display: 'flex',
              alignItems: 'center',
              gap: theme.fuel6Tokens.JddcSpacingXSmall
            })}
          >
            <Typography variant="labelMedium" sx={{ fontWeight: 'bold' }}>
              {firstLevelLabel}
            </Typography>
            {createScope && (
              <Box
                component="span"
                aria-label="required field"
                sx={{ color: 'error.main' }}
              >
                *
              </Box>
            )}
          </Box>
        )}
        {masterData.map((currentRecord, index) => {
          return (
            <Box key={currentRecord.code}>
              {index >= startIndex &&
                index <= endIndex &&
                paintTree(currentRecord, index)}
            </Box>
          );
        })}
      </>
    );
  };

  const getHighlightedText = (value) => {
    if (filterText !== '') {
      let regex = new RegExp('(' + filterText + ')', 'gi');
      const parts = value.split(regex);

      return (
        <span>
          {parts.map((part, index) =>
            part.toLowerCase() === filterText ? (
              <b key={`${part}-${index}`}>{part}</b>
            ) : (
              <span key={`${part}-${index}`}>{part}</span>
            )
          )}
        </span>
      );
    } else return value;
  };

  function getResetFilteredTreeNode(item) {
    item.hidden = false;

    if (item.children) {
      item.children.forEach((child) => getResetFilteredTreeNode(child));
      item.caretOpen = false;
      item.disabled = false;
    }
  }
  function getFilteredTreeNode(item, lowercaseSearchtext) {
    let itemMatch =
      item.code.toLowerCase().indexOf(lowercaseSearchtext) >= 0 ||
      item.name.toLowerCase().indexOf(lowercaseSearchtext) >= 0;

    // Hide item
    item.hidden = true;

    if (itemMatch) {
      // Show All Children
      getResetFilteredTreeNode(item);
      if (item.children) {
        // Find if any children also matches
        let anyChildMatches = false;

        for (let child of item.children) {
          if (child.name.toLowerCase().indexOf(lowercaseSearchtext) >= 0) {
            anyChildMatches = true;
            break;
          }
        }
        item.caretOpen = anyChildMatches;
        item.disabled = false;
      }
    } else if (item.children) {
      item.children.forEach((child) =>
        getFilteredTreeNode(child, lowercaseSearchtext)
      );
      const hiddenList = item.children.map((child) => child.hidden);
      const allHidden = hiddenList.every(Boolean);

      if (allHidden) item.hidden = true;
      else {
        item.disabled = true;
        item.caretOpen = true;
        item.hidden = false;
      }
    }
  }
  const filterHandler = (searchText) => {
    let listForFilter = [...masterData];
    setFilterText(searchText);

    let resetFilter = searchText === '';
    let count = 0;

    listForFilter.forEach((item) => {
      resetFilter
        ? getResetFilteredTreeNode(item)
        : getFilteredTreeNode(item, searchText);
      // Count non-hidden
      !item.hidden && count++;
    });
    setMasterData(listForFilter);
    setErrorMessage(
      count === 0
        ? 'No results found. Please try a new search term or expanding your filter criteria.'
        : ''
    );
  };

  return (
    <Box sx={{ width: '100%' }}>
      {!readOnly && showFilter && (
        <Box
          sx={{
            width: '100%',
            display: 'flex',
            flexDirection: 'column'
          }}
        >
          {selectAllFlag && (
            <Box
              component="span"
              onClick={selectAll}
              sx={{
                color: 'success.main',
                cursor: 'pointer',
                textDecoration: 'underline'
              }}
            >
              Select All
            </Box>
          )}
          <Search
            size="small"
            sx={{ width: '100%', display: 'block' }}
            id={filterSearchId}
            placeholder={filterPlaceHolder}
            slotProps={{
              search: {
                name: 'search',
                value: activeFilterValue,
                onChange: (e) => {
                  const nextFilterValue = e.target.value.trim().toLowerCase();
                  filterHandler(nextFilterValue);
                  onFilterValueChange && onFilterValueChange(nextFilterValue);
                }
              }
            }}
          />
          {errorMessage && (
            <Typography
              variant="bodySmall"
              sx={{
                color: 'error.main',
                display: 'block',
                width: '100%',
                whiteSpace: 'normal',
                overflowWrap: 'anywhere'
              }}
            >
              {errorMessage}
            </Typography>
          )}
        </Box>
      )}

      <Box
        sx={(theme) => ({
          ...(addVScrollBar && { overflowY: 'auto', maxHeight: '300px' }),
          ...(invalidError && {
            p: theme.fuel6Tokens.JddcSpacingMedium,
            border: '1px solid',
            borderColor: 'error.main'
          })
        })}
      >
        {showInOneColumn ? (
          <Box sx={(theme) => ({ pt: theme.fuel6Tokens.JddcSpacingMedium })}>
            {displayCheckbox('justOneCol')}
          </Box>
        ) : (
          <Box sx={{ display: 'flex' }}>
            <Box sx={{ flex: 1 }}>{displayCheckbox('left')}</Box>
            <Box sx={{ flex: 1 }}>{displayCheckbox('right')}</Box>
          </Box>
        )}
        {showFilterInSameCol && (
          <Box
            sx={{
              width: '100%',
              maxWidth: '100%',
              display: 'flex',
              flexDirection: 'column'
            }}
          >
            <Search
              size="small"
              sx={{ width: '100%', display: 'block' }}
              id={sameColFilterSearchId}
              placeholder={filterPlaceHolder}
              slotProps={{
                search: {
                  value: activeFilterValue,
                  onChange: (e) => {
                    const nextFilterValue = e.target.value.trim().toLowerCase();
                    filterHandler(nextFilterValue);
                    onFilterValueChange && onFilterValueChange(nextFilterValue);
                  }
                }
              }}
            />
            {errorMessage && (
              <Typography
                variant="bodySmall"
                sx={{
                  color: 'error.main',
                  display: 'block',
                  width: '100%',
                  whiteSpace: 'normal',
                  overflowWrap: 'anywhere'
                }}
              >
                {errorMessage}
              </Typography>
            )}
          </Box>
        )}
      </Box>
      {invalidError && <FormHelperText error>{invalidError}</FormHelperText>}
      {!readOnly && !donotShowClearButton && (
        <Box
          sx={(theme) => ({
            mt: theme.fuel6Tokens.JddcSpacingLarge,
            display: 'flex',
            justifyContent: 'flex-end'
          })}
        >
          <Button variant="secondary" onClick={clearAll}>
            Clear
          </Button>
        </Box>
      )}
    </Box>
  );
}
